/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * COUNTRY POLICY STARTING POINT (v1_397).
 *
 * A blank country policy is safe but useless: geography rates Low for anywhere
 * FATF has not listed, and a trial tenant will never open a settings screen to
 * type country names into a box. So the firm is shown a list with every
 * jurisdiction already placed, and moves what it disagrees with.
 *
 * NOTHING HERE IS APPLIED UNTIL THE MLRO SAVES IT. A suggested placement that
 * took effect on its own would be CompliantID making a risk judgement on behalf
 * of a regulated firm, which is the one thing the product says it does not do.
 * The MLRO's act of accepting is what makes it the firm's policy, and that act
 * is what a regulator will ask about.
 *
 * EVERY SUGGESTION CARRIES ITS REASON, and every reason is a published finding
 * rather than our opinion. Mostly that is FATF Immediate Outcome 3: where FATF
 * or a regional body assessed supervision as having low effectiveness, "high
 * risk" is a defensible starting position and the MLRO can say why. Where we
 * have no such finding, the suggestion is Standard and says so.
 *
 * FATF-LISTED JURISDICTIONS ARE NOT IN THIS LIST. The country-policy suite
 * asserts this against the live FATF data, which is how five jurisdictions were
 * caught in the first draft: the British Virgin Islands, Monaco, Vietnam,
 * Venezuela and Kenya were all on increased monitoring as at 19/06/26 and were
 * removed. WHEN THE FATF LISTS ARE REFRESHED, run that suite: a jurisdiction
 * that comes OFF a list needs adding here, and one that goes ON needs removing. Layer 1 already rates them
 * High automatically on every case and a firm cannot lower them. Putting them
 * here would imply they were the firm's choice, and invite someone to try.
 */

/**
 * Jurisdictions a compliance officer in payments, FX, funds or virtual assets
 * actually encounters. Deliberately not all 198: a policy nobody reads is worse
 * than no policy, and the free-text box covers the rest.
 *
 * suggested: 'prohibited' | 'high' | 'medium' | 'standard'
 */
import { lowEffectivenessJurisdictions } from './supervisory-effectiveness.js';

/**
 * FATF-listed jurisdictions never appear in the picker: they are High by law
 * whatever the firm chooses, and the modal says so. This exclusion set must
 * mirror the lists in api/_country-lists.js (which src/ cannot import); a
 * test in tests/country-policy.test.mjs cross-checks the two at every run,
 * so an update to one without the other fails the build.
 */
export const FATF_LISTED_EXCLUDED = [
  'Iran', "Democratic People's Republic of Korea", 'Myanmar',
  'Angola', 'Bolivia', 'Bosnia and Herzegovina', 'Bulgaria', 'Cameroon',
  "C\u00f4te d'Ivoire", 'Democratic Republic of the Congo', 'Haiti', 'Iraq',
  'Kenya', 'Kuwait', 'Lao PDR', 'Lebanon', 'Monaco', 'Nepal',
  'Papua New Guinea', 'South Sudan', 'Syria', 'Venezuela', 'Vietnam',
  'Virgin Islands (UK)', 'Yemen'
];

const CURATED_COUNTRIES = [
  // Supervision assessed as LOW effectiveness. Suggested High.
  { country: 'Cayman Islands', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Oct/21)' },
  { country: 'Turks and Caicos Islands', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Nov/23)' },
  { country: 'Saint Kitts and Nevis', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Feb/22)' },
  { country: 'Saint Lucia', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Feb/24)' },
  { country: 'Antigua and Barbuda', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Nov/21)' },
  { country: 'Curacao', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Jul/25)' },
  { country: 'Sint Maarten', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Jul/25)' },
  { country: 'Suriname', suggested: 'high', reason: 'FATF rated supervision low effectiveness (CFATF, Nov/23)' },
  { country: 'Mauritius', suggested: 'high', reason: 'FATF rated supervision low effectiveness (ESAAMLG, Jan/23)' },
  { country: 'Seychelles', suggested: 'high', reason: 'FATF rated supervision low effectiveness (ESAAMLG, Apr/24)' },
  { country: 'Malta', suggested: 'high', reason: 'FATF rated supervision low effectiveness (MONEYVAL, Aug/21)' },
  { country: 'Vanuatu', suggested: 'high', reason: 'FATF rated supervision low effectiveness (APG, Sep/18)' },
  { country: 'Samoa', suggested: 'high', reason: 'FATF rated supervision low effectiveness (APG, Jun/23)' },
  { country: 'Nauru', suggested: 'high', reason: 'FATF rated supervision low effectiveness (APG, Feb/25)' },
  { country: 'Marshall Islands', suggested: 'high', reason: 'Offshore financial centre; supervision rated moderate (APG, Nov/24)' },
  { country: 'Pakistan', suggested: 'high', reason: 'FATF rated supervision low effectiveness (APG, Aug/22)' },
  { country: 'Sri Lanka', suggested: 'high', reason: 'FATF rated supervision low effectiveness (APG, Nov/21)' },
  { country: 'Cambodia', suggested: 'high', reason: 'FATF rated supervision low effectiveness (APG, Oct/23)' },
  { country: 'Azerbaijan', suggested: 'high', reason: 'FATF rated supervision low effectiveness (MONEYVAL, Feb/24)' },
  { country: 'Tunisia', suggested: 'high', reason: 'FATF rated supervision low effectiveness (MENAFATF, Jan/20)' },
  { country: 'Nicaragua', suggested: 'high', reason: 'FATF rated supervision low effectiveness (GAFILAT, Jan/21)' },
  { country: 'Dominican Republic', suggested: 'high', reason: 'FATF rated supervision low effectiveness (GAFILAT, Sep/19)' },
  { country: 'El Salvador', suggested: 'high', reason: 'FATF rated supervision low effectiveness (GAFILAT, Aug/24)' },
  { country: 'Nigeria', suggested: 'high', reason: 'Supervision rated moderate; significant fraud and cyber-enabled crime exposure (GIABA, Nov/24)' },
  { country: 'Zimbabwe', suggested: 'high', reason: 'FATF rated supervision low effectiveness (ESAAMLG, Apr/24)' },
  { country: 'Denmark', suggested: 'medium', reason: 'FATF rated supervision low effectiveness (FATF, Feb/21), though the wider regime rates well' },
  { country: 'Finland', suggested: 'medium', reason: 'FATF rated supervision low effectiveness (FATF, Oct/23), though the wider regime rates well' },
  { country: 'Iceland', suggested: 'medium', reason: 'FATF rated supervision low effectiveness (FATF, Sep/21)' },

  // Offshore or secrecy exposure without a low supervision finding.
  { country: 'Belize', suggested: 'medium', reason: 'Offshore company formation centre; supervision rated moderate (CFATF, Jan/25)' },
  { country: 'Panama', suggested: 'medium', reason: 'Offshore company formation centre; supervision rated moderate (GAFILAT, Aug/19)' },
  { country: 'Bahamas', suggested: 'medium', reason: 'Offshore financial centre; supervision rated moderate (CFATF, Dec/22)' },
  { country: 'Saint Vincent and the Grenadines', suggested: 'medium', reason: 'Offshore financial centre; supervision rated moderate (CFATF, Jan/24)' },
  { country: 'Barbados', suggested: 'medium', reason: 'Offshore financial centre; supervision rated moderate (CFATF, Feb/21)' },
  { country: 'Bermuda', suggested: 'standard', reason: 'Offshore centre, but FATF rated supervision substantial (CFATF, Jan/20)' },
  { country: 'Gibraltar', suggested: 'medium', reason: 'Offshore financial centre; supervision rated moderate (MONEYVAL, May/24)' },
  { country: 'United Arab Emirates', suggested: 'medium', reason: 'Major trade and remittance hub; supervision rated moderate (MENAFATF, Jul/23)' },
  { country: 'Turkey', suggested: 'medium', reason: 'Supervision rated moderate; significant cross-border cash and trade exposure (FATF, Jul/23)' },
  { country: 'Cyprus', suggested: 'medium', reason: 'Corporate services concentration; supervision rated moderate (MONEYVAL, Dec/23)' },
  { country: 'Russia', suggested: 'prohibited', reason: 'Extensive sanctions exposure; FATF membership suspended February 2023' },
  { country: 'Belarus', suggested: 'prohibited', reason: 'Extensive sanctions exposure' },

  // Rated well. Listed so a firm can see they were considered, not omitted.
  { country: 'Ireland', suggested: 'standard', reason: 'FATF rated supervision substantial (FATF, Feb/22)' },
  { country: 'United Kingdom', suggested: 'standard', reason: 'FATF rated supervision moderate; strong wider regime (FATF, Jun/22)' },
  { country: 'United States', suggested: 'standard', reason: 'FATF rated supervision moderate; strong wider regime (FATF, Mar/24)' },
  { country: 'Switzerland', suggested: 'standard', reason: 'FATF rated supervision moderate (FATF, Oct/23)' },
  { country: 'Singapore', suggested: 'standard', reason: 'FATF rated supervision moderate (FATF/APG, Nov/19)' },
  { country: 'Hong Kong', suggested: 'standard', reason: 'FATF rated supervision moderate (FATF, Feb/23)' },
  { country: 'Luxembourg', suggested: 'standard', reason: 'FATF rated supervision moderate (FATF, Sep/23)' },
  { country: 'Netherlands', suggested: 'standard', reason: 'FATF rated supervision moderate (FATF, Sep/25)' },
  { country: 'Germany', suggested: 'standard', reason: 'FATF rated supervision moderate (FATF, Dec/23)' },
  { country: 'Lithuania', suggested: 'standard', reason: 'FATF rated supervision moderate; large EMI population (MONEYVAL, Dec/23)' },
  { country: 'Estonia', suggested: 'standard', reason: 'FATF rated supervision moderate (MONEYVAL, Dec/24)' },
  { country: 'Jersey', suggested: 'standard', reason: 'FATF rated supervision moderate (MONEYVAL, May/24)' },
  { country: 'Guernsey', suggested: 'standard', reason: 'FATF rated supervision moderate (MONEYVAL, Dec/24)' },
  { country: 'Isle of Man', suggested: 'standard', reason: 'FATF rated supervision moderate (MONEYVAL, Dec/22)' }
];

/**
 * DERIVED entries (v1_457). The platform holds the FATF/FSRB effectiveness
 * dataset for 198 jurisdictions, so the catalogue is derived from it rather
 * than curated by hand: every jurisdiction rated LOW effectiveness that is
 * not already curated above and not FATF-listed appears here, suggested
 * High, with its citation generated from the dataset row. A hand-curated
 * list is exactly how Mauritius's neighbours went missing (08/08/26).
 * Matching tolerates the small naming differences between the two datasets
 * (accents, "Lao PDR" vs "Laos", "Congo" vs the full form) by folding names
 * before comparison.
 */
const fold = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]+/g, ' ').trim();
const CURATED_NAMES = new Set(CURATED_COUNTRIES.map(c => fold(c.country)));
const EXCLUDED_NAMES = new Set(FATF_LISTED_EXCLUDED.map(fold));
// Naming differences between the FATF statements and the dataset: same
// country, two spellings. The picker-leak test in country-policy.test.mjs
// catches any new one.
EXCLUDED_NAMES.add(fold('Laos'));                    // grey list: 'Lao PDR'
EXCLUDED_NAMES.add(fold('British Virgin Islands'));  // grey list: 'Virgin Islands (UK)'

const DERIVED_COUNTRIES = lowEffectivenessJurisdictions()
  .filter(j => !CURATED_NAMES.has(fold(j.jurisdiction)) && !EXCLUDED_NAMES.has(fold(j.jurisdiction)))
  .map(j => ({
    country: j.jurisdiction,
    suggested: 'high',
    reason: 'FATF rated supervision low effectiveness (' + j.assessedBy + ', ' + j.reportDate + ')',
    derived: true
  }))
  .sort((a, b) => a.country.localeCompare(b.country));

export const SUGGESTED_COUNTRIES = [...CURATED_COUNTRIES, ...DERIVED_COUNTRIES];

export const PLACEMENTS = ['standard', 'medium', 'high', 'prohibited'];

export const PLACEMENT_LABEL = {
  standard: 'No firm view',
  medium: 'Medium risk',
  high: 'High risk',
  prohibited: 'Will not accept'
};

export const PLACEMENT_HELP = {
  standard: 'Rated on the FATF lists alone, as now.',
  medium: 'Geography rates Medium on any case touching this country.',
  high: 'Geography rates High on any case touching this country.',
  prohibited: 'A case touching this country stops and comes to the MLRO.'
};

/** Turn a placement map into the policy shape the engine reads. */
export function placementsToPolicy(placements) {
  const out = { prohibited: [], high: [], medium: [] };
  for (const [country, placement] of Object.entries(placements || {})) {
    if (out[placement]) out[placement].push(country);
  }
  return out;
}

/** Turn a stored policy back into a placement map for the screen. */
export function policyToPlacements(policy) {
  const map = {};
  for (const c of SUGGESTED_COUNTRIES) map[c.country] = 'standard';
  for (const p of ['medium', 'high', 'prohibited']) {
    for (const country of ((policy && policy[p]) || [])) map[country] = p;
  }
  return map;
}

/** The suggested placement map, before the MLRO has touched anything. */
export function suggestedPlacements() {
  const map = {};
  for (const c of SUGGESTED_COUNTRIES) map[c.country] = c.suggested;
  return map;
}

/** Countries in a saved policy that are not in the curated list. */
export function extraCountries(policy) {
  const known = new Set(SUGGESTED_COUNTRIES.map(c => c.country.toLowerCase()));
  const out = [];
  for (const p of ['medium', 'high', 'prohibited']) {
    for (const country of ((policy && policy[p]) || [])) {
      if (!known.has(String(country).toLowerCase())) out.push({ country, placement: p });
    }
  }
  return out;
}
